import { useState, useCallback, useRef, useEffect } from "react";
import type { ColumnDef } from "./types";
import {
  fmtCurrencyDec,
  fmtDate,
  fmtNumber,
  fmtDecimal,
  fmtPercent,
} from "../../utils/format";

// ---------- helpers ----------

export function displayValue(value: unknown, col: ColumnDef): string {
  if (value == null) return "\u2014";
  switch (col.type) {
    case "currency":
      return fmtCurrencyDec(Number(value));
    case "number":
      return col.decimals != null && col.decimals > 0
        ? fmtDecimal(Number(value))
        : fmtNumber(Number(value));
    case "year":
      return String(Math.round(Number(value)));
    case "percent":
      return fmtPercent(Number(value));
    case "date":
      return fmtDate(String(value));
    case "checkbox":
      return value ? "Yes" : "No";
    default:
      return String(value);
  }
}

function parseInputValue(raw: string, type: ColumnDef["type"]): unknown {
  switch (type) {
    case "year":
      return raw === "" ? null : parseInt(raw, 10);
    case "number":
    case "currency":
    case "percent":
      return raw === "" ? null : Number(raw);
    case "checkbox":
      return raw === "true";
    default:
      return raw;
  }
}

// ---------- component ----------

interface EditableCellProps {
  col: ColumnDef;
  value: unknown;
  isManual: boolean;
  onChange: (val: unknown) => void;
  onNavigate: (dir: "down" | "right" | "left") => void;
}

export default function EditableCell({
  col,
  value,
  isManual,
  onChange,
  onNavigate,
}: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);

  const startEdit = useCallback(() => {
    if (col.editable === false) return;
    setDraft(value == null ? "" : String(value));
    setEditing(true);
  }, [col.editable, value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [editing]);

  const commit = useCallback(() => {
    const parsed = parseInputValue(draft, col.type);
    onChange(parsed);
    setEditing(false);
  }, [draft, col.type, onChange]);

  const cancel = useCallback(() => {
    setEditing(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commit();
        onNavigate("down");
      } else if (e.key === "Tab") {
        e.preventDefault();
        commit();
        onNavigate(e.shiftKey ? "left" : "right");
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      }
    },
    [commit, cancel, onNavigate],
  );

  const isNumeric =
    col.type === "number" || col.type === "currency" || col.type === "percent" || col.type === "year";
  const bgClass = isManual ? "bg-honey-50" : "";

  // Non-editable cell
  if (col.editable === false) {
    return (
      <td
        className={`px-3 py-1.5 text-[13px] tabular-nums text-bark-500 whitespace-nowrap ${
          isNumeric ? "text-right" : "text-left"
        }`}
      >
        {displayValue(value, col)}
      </td>
    );
  }

  // Checkbox
  if (col.type === "checkbox") {
    return (
      <td className={`px-3 py-1.5 text-center ${bgClass}`}>
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 rounded border-grove-300 text-forest-600 focus:ring-forest-500"
        />
      </td>
    );
  }

  // Dropdown
  if (col.type === "dropdown" && col.options) {
    return (
      <td className={`px-3 py-1.5 ${bgClass}`}>
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-1 py-0.5 text-[13px] bg-transparent border-0 border-b border-transparent focus:border-forest-500 focus:ring-0 text-bark-700"
        >
          <option value="">--</option>
          {col.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </td>
    );
  }

  // Date
  if (col.type === "date") {
    return (
      <td className={`px-3 py-1.5 ${bgClass}`}>
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="date"
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full px-1 py-0.5 text-[13px] bg-transparent border-0 border-b border-transparent focus:border-forest-500 focus:ring-0 text-bark-700"
        />
      </td>
    );
  }

  // Text / number / currency / percent: inline editing
  if (editing) {
    return (
      <td className={`px-3 py-1 ${bgClass}`}>
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={isNumeric ? "number" : "text"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          step={isNumeric ? "any" : undefined}
          className={`w-full px-1 py-0.5 text-[13px] bg-white border border-forest-500 rounded focus:outline-none focus:ring-1 focus:ring-forest-500 text-bark-800 ${
            isNumeric ? "text-right tabular-nums" : "text-left"
          }`}
        />
      </td>
    );
  }

  return (
    <td
      className={`px-3 py-1.5 cursor-pointer hover:bg-grove-100 transition-colors ${bgClass} ${
        isNumeric ? "text-right" : "text-left"
      }`}
      onDoubleClick={startEdit}
      onClick={startEdit}
    >
      <span className="text-[13px] tabular-nums text-bark-700 whitespace-nowrap">
        {displayValue(value, col)}
      </span>
    </td>
  );
}
